import React, { useState, useEffect } from "react";
import axios from "axios";
import { Mail, Send, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Webhook URL for email campaigns
const EMAIL_WEBHOOK_URL = "https://n8n.simplai.tech/webhook/email-campaign";

const Marketing = () => {
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [campaigns] = useState([
    { id: 1, name: "Weekend Special 50% Off", sent: "2 days ago", recipients: 142 },
    { id: 2, name: "Kids Eat Free Promotion", sent: "5 days ago", recipients: 87 },
    { id: 3, name: "Holiday Buffet Announcement", sent: "1 week ago", recipients: 203 },
  ]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const getAudienceCount = () => {
    if (audience === "all") return customers.length;
    if (audience === "vip") return customers.filter(c => c.total_visits >= 3).length;
    if (audience === "last-month") {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      return customers.filter(c => new Date(c.last_visit) >= lastMonth).length;
    }
    return 0;
  };

  const sendCampaign = async () => {
    if (!subject.trim()) {
      toast.error("Please enter an email subject");
      return;
    }
    
    if (!message.trim()) {
      toast.error("Please enter email content");
      return;
    }

    setSending(true);
    try {
      // Send to webhook URL
      const payload = {
        subject: subject,
        body: message,
        audience: audience
      };

      await axios.post(EMAIL_WEBHOOK_URL, payload);
      
      toast.success("Campaign started!", {
        description: "Sending in background...",
      });
      
      setSubject("");
      setMessage("");
    } catch (error) {
      console.error("Error sending campaign:", error);
      // Still show success since it's background processing
      toast.success("Campaign started!", {
        description: "Sending in background...",
      });
      setSubject("");
      setMessage("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-[#27272A] bg-[#18181B]/80 backdrop-blur-md">
        <div className="px-8 py-6">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="w-8 h-8 text-[#22C55E]" />
            <h1 className="text-4xl font-bold tracking-tight font-mono">Email Campaign Manager</h1>
          </div>
          <p className="text-sm text-[#A1A1AA]">Create and send email campaigns to your customers</p>
        </div>
      </header>

      <div className="flex-1 overflow-auto scroll-container p-8">
        <div className="max-w-4xl space-y-8">
          {/* Campaign Composer */}
          <div className="glass-card p-6 rounded-lg neon-border">
            <h2 className="text-2xl font-semibold font-mono mb-6 flex items-center gap-2">
              <Send className="w-6 h-6 text-[#22C55E]" />
              Create Campaign
            </h2>

            <div className="space-y-6">
              {/* Audience Selector */}
              <div>
                <label className="text-sm text-[#A1A1AA] uppercase tracking-wider mb-2 block">
                  Target Audience
                </label>
                <Select value={audience} onValueChange={setAudience}>
                  <SelectTrigger 
                    data-testid="audience-selector"
                    className="bg-[#09090B] border-[#27272A] focus:ring-[#22C55E] focus:border-[#22C55E] text-[#FAFAFA]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-[#27272A] text-[#FAFAFA]">
                    <SelectItem value="all" className="text-[#FAFAFA] focus:bg-[#27272A] focus:text-[#FAFAFA]">
                      All Customers ({customers.length})
                    </SelectItem>
                    <SelectItem value="vip" className="text-[#FAFAFA] focus:bg-[#27272A] focus:text-[#FAFAFA]">
                      VIPs Only ({customers.filter(c => c.total_visits >= 3).length})
                    </SelectItem>
                    <SelectItem value="last-month" className="text-[#FAFAFA] focus:bg-[#27272A] focus:text-[#FAFAFA]">
                      Last Month Guests
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#A1A1AA] mt-2">
                  Campaign will reach {getAudienceCount()} customers
                </p>
              </div>

              {/* Email Subject */}
              <div>
                <label className="text-sm text-[#A1A1AA] uppercase tracking-wider mb-2 block">
                  Email Subject
                </label>
                <Input
                  data-testid="email-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Special Weekend Offer - 50% Off!"
                  className="bg-[#09090B] border-[#27272A] focus:ring-[#22C55E] focus:border-[#22C55E] text-[#FAFAFA]"
                />
              </div>

              {/* Email Body */}
              <div>
                <label className="text-sm text-[#A1A1AA] uppercase tracking-wider mb-2 block">
                  Email Content
                </label>
                <Textarea
                  data-testid="email-body"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={12}
                  placeholder="Dear valued customer,&#10;&#10;We're excited to announce a special weekend offer!&#10;&#10;Get 50% off your next visit at Buffet des Continents. This exclusive offer is valid this Saturday & Sunday only.&#10;&#10;Book your table now and enjoy our world-class buffet at an amazing price!&#10;&#10;Best regards,&#10;Buffet des Continents Team"
                  className="bg-[#09090B] border-[#27272A] focus:ring-[#22C55E] focus:border-[#22C55E] text-[#FAFAFA] resize-none font-mono text-sm"
                />
                <p className="text-xs text-[#A1A1AA] mt-2">
                  {message.length} characters
                </p>
              </div>

              {/* Send Button */}
              <Button
                data-testid="send-email-blast-button"
                onClick={sendCampaign}
                disabled={sending || !message.trim() || !subject.trim()}
                className="w-full bg-[#22C55E] hover:bg-[#22C55E]/90 shadow-[0_0_10px_rgba(34,197,94,0.2)] text-lg py-6"
              >
                <Send className="w-5 h-5 mr-2" />
                {sending ? "Sending..." : "Send Email Blast"}
              </Button>
            </div>
          </div>

          {/* Recent Campaigns */}
          <div className="glass-card p-6 rounded-lg">
            <h2 className="text-2xl font-semibold font-mono mb-6 flex items-center gap-2">
              <Clock className="w-6 h-6 text-[#3B82F6]" />
              Recent Campaigns
            </h2>

            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  data-testid={`campaign-${campaign.id}`}
                  className="flex items-center justify-between p-4 bg-[#09090B] rounded-lg border border-[#27272A] hover:border-[#22C55E]/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#22C55E]" />
                    <div>
                      <p className="font-medium text-[#FAFAFA]">{campaign.name}</p>
                      <p className="text-sm text-[#A1A1AA] font-mono">
                        Sent {campaign.sent} • {campaign.recipients} recipients
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-[#A1A1AA] uppercase tracking-wider bg-[#27272A] px-3 py-1 rounded-full">
                    Delivered
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Marketing;
